import _ from 'lodash';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import BlazeInputReactSelect from 'blaze-input-react-select';
import { FormInputLabel } from 'blaze-form';
import { Button, FormControl, FormCheck, FormGroup, Col, Row, Alert } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import { modal } from '../modal/ModalEmitter';
import { glCodeFieldName } from './util';
import toastr from '../toastr';
import LoadingPage from '../LoadingPage';
import FileNotFound from '../FileNotFound';
import ModalTable from '../modal/ModalTable';
import FormInputCalendarItem from '../form/control/FormInputCalendarItem';
import FormInputListSearch from '../form/control/FormInputListSearch';
import { getListItemId } from '../../util/form';

const PostToAccounting = ({ deal, quoteId, callback }) => {
  const { user } = useSelector(state => state.app);

  const today = moment().format('MM-DD-YYYY');
  const [postingDate, setPostingDate] = useState(today);
  const [postingSource, setPostingSource] = useState('');
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [excludeZeros, setExcludeZeros] = useState(true);
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [options, setOptions] = useState([]);
  const [mappingValue, setMappingValue] = useState(null);
  const [fieldLookup, setFieldLookup] = useState([]);
  const [postLog, setPostLog] = useState([]);
  const [postingData, setPostingData] = useState();
  const [postingTotal, setPostingTotal] = useState(0);
  const [postingAddendum, setPostingAddendum] = useState({});
  const [accounts, setAccounts] = useState([]); // For Account Lookup

  const [postingBranch, setPostingBranch] = useState(deal.branchId || user.branch.id);

  const fetchMappings = () => {
    return ApiService.get(`crm/mappings?branchId=${String(postingBranch)}`).then(response => {
      const { mappings, mappedFields, accountsUsed } = response.data;
      setOptions(mappings || []);
      setFieldLookup(mappedFields);
      setAccounts(accountsUsed);
    });
  };

  // Gets all the things
  const fetchAccounting = () => {
    return ApiService.get(`crm/quote/${quoteId}/accounting?branchId=${String(postingBranch)}`).then(response => {
      const { mappings, mappedFields } = response.data;
      setOptions(mappings || []);
      setFieldLookup(mappedFields);
      setQuote(response.data.quote);
      setPostLog(response.data.postLog);
    });
  };

  const handleCurrentBranchChange = event => {
    setPostingBranch(Number(event.target.value));
  };

  const handlePostingDateChange = (fieldName, value) => {
    setPostingDate(value);
  };

  const handlePostingSourceSelected = (suggestion, value) => {
    setPostingSource(getListItemId(value));
  };

  const handleMappingChange = (fieldName, value) => {
    setMappingValue(value);
    setReadyToSubmit(false);
  };

  const handleNewMapping = () => {
    const condition = quote.Vehicle['New-Used'] === 'New' ? 'N' : 'U';
    const type = quote.Vehicle.Type;

    modal.open('dbEditItem', {
      action: 'new',
      hideFooter: true,
      hidePrint: true,
      itemName: 'EQS4',
      size: 'lg',
      staticBackdrop: true,
      title: 'Add New Mapping',
      submitCallback: response => {
        fetchMappings().then(() => {
          setMappingValue(response.data.id);
        });
      },
      onClose: () => {},
      query: { condition, type, branchId: String(postingBranch) },
    });
  };

  const handleEditMapping = () => {
    modal.open('dbEditItem', {
      action: 'edit',
      hideFooter: true,
      hidePrint: true,
      itemName: 'EQS4',
      lookupKey: String(mappingValue),
      size: 'lg',
      staticBackdrop: true,
      title: 'Edit Mapping',
      submitCallback: () => {
        fetchMappings();
      },
      onClose: () => {},
    });
  };

  const handlePost = () => {
    return ApiService.post(`crm/quote/${quoteId}/post`, {
      quoteId,
      mappingValue,
      postingDate,
      postingSource,
      postingData,
      postingBranch,
      postingAddendum,
    })
      .then(() => {
        if (callback) {
          callback();
        }
      })
      .catch(({ response }) => {
        toastr.error('Posting Failed', response.data.error);
      });
  };

  const refresh = () => {
    if (quote && quote.Vehicle) {
      const condition = quote.Vehicle['New-Used'] === 'New' ? 'N' : 'U';
      const type = quote.Vehicle.Type;
      let found;

      // First check given gl codes
      for (const option of options) {
        if (option.glcode) {
          const glCodeValues = option.glcode.split('~!~');
          for (const glCodeValue of glCodeValues) {
            if (quote.Vehicle[glCodeFieldName] === glCodeValue) {
              found = option;
            }
          }
        }
      }

      // Then vehicle type & condition
      if (!found) {
        found = options.find(o => o.condition === condition && o.type === type);
      }

      if (found && !mappingValue) {
        setMappingValue(found.id);
      }
    }
  };

  const getGlAccount = accountNum => {
    const record = accounts.find(a => a.id === accountNum);
    if (!record) {
      return accountNum;
    }

    return `${accountNum}: ${record.value}`;
  };

  const compileData = () => {
    const data = [];
    let runningTotal = 0;

    const mappingEntry = options.find(o => o.id === mappingValue);

    if (!mappingEntry) {
      console.warn('Mapping Entry not found', mappingValue);
      return;
    }

    const mappingData = mappingEntry.mapping;
    const dealId = quote.dealId;

    // Quote Mapping option for Quote Number is 'quoteId' should probably be 'id'
    if (!quote.quoteId) {
      quote.quoteId = quote.id;
    }

    if (!mappingData.field || !mappingData.field.length) {
      console.warn('Mapping Entry data is empty', mappingData);
      return;
    }

    mappingData.field.forEach((fieldName, index) => {
      const isDebit = mappingData.creditDebit[index] === 'D';
      // Check For Misc Fees or the like
      if (/:/.test(fieldName)) {
        const [lineItemFieldName, lineItemFieldNameValue, lineItemAmountFieldName] = fieldName.split(':');
        const lineItemField = fieldLookup.find(f => f.name === fieldName);
        // Find row in table

        if (!_.get(quote, lineItemFieldName)) {
          console.warn(`Invalid fieldname ${lineItemFieldName}`);
          return;
        }

        if (!_.get(quote, lineItemAmountFieldName)) {
          console.warn(`Invalid fieldname ${lineItemAmountFieldName}`);
          return;
        }

        const lineItems = _.get(quote, lineItemFieldName);
        const lineItemValues = _.get(quote, lineItemAmountFieldName);

        let fieldValue;

        // Write-Ins
        if (String(lineItemFieldNameValue) === '0') {
          let writeInTotal = 0;
          lineItems.forEach((lineItem, lineItemIndex) => {
            if (!/\d+/.test(lineItem)) {
              writeInTotal += Math.round(lineItemValues[lineItemIndex] * 100);
            }
          });
          fieldValue = writeInTotal / 100;
        } else {
          const valueIndex = lineItems.findIndex(e => String(e) === String(lineItemFieldNameValue));
          fieldValue = Number(lineItemValues[valueIndex] || 0);
        }

        const amount = !isDebit && fieldValue !== 0 ? fieldValue * -1 : fieldValue;

        // Zeros don't need to be posted, clutters books, crts
        if (!excludeZeros || (excludeZeros && amount !== 0)) {
          data.push([
            lineItemField.label,
            getGlAccount(mappingData.account[index]),
            quote[mappingData.control[index]],
            dealId,
            amount,
            isDebit,
          ]);
        }

        runningTotal += Math.round(amount * 100);
        return;
      }

      const field = fieldLookup.find(f => f.name === fieldName);

      if (field) {
        if (field.insideTable) {
          const fieldValue = _.get(quote, fieldName);
          const controlValues = _.get(quote, field.accountingControl);

          // Aggregate values based on control number (salesperson id)
          if (fieldName === 'data.commission.amount') {
            const commissionAddendum = []; // Used to update the unit record
            const controlValueLookup = {};
            controlValues.forEach((controlValue, controlIndex) => {
              if (!controlValueLookup[controlValue]) {
                controlValueLookup[controlValue] = [];
              }

              controlValueLookup[controlValue].push(controlIndex);
            });

            Object.keys(controlValueLookup).forEach(controlValue => {
              const valueIndices = controlValueLookup[controlValue];
              let controlTotal = 0;
              valueIndices.forEach(valueIndex => {
                const amount =
                  !isDebit && Number(fieldValue[valueIndex]) !== 0
                    ? Number(fieldValue[valueIndex]) * -1
                    : Number(fieldValue[valueIndex]);
                controlTotal += Math.round(amount * 100);
              });

              const postAmount = controlTotal / 100;
              data.push([field.label, getGlAccount(mappingData.account[index]), controlValue, dealId, postAmount, isDebit]);

              // Move the sign back if it was a Credit for the addendum
              const realAmount = !isDebit && postAmount !== 0 ? postAmount * -1 : postAmount;

              commissionAddendum.push({ userId: controlValue, amount: realAmount });
              runningTotal += Math.round(controlTotal);
            });
            setPostingAddendum(c => ({ ...c, commissionAddendum }));
          } else {
            if (fieldValue && fieldValue.length) {
              fieldValue.forEach((value, tableIndex) => {
                const amount = !isDebit && Number(value) !== 0 ? Number(value) * -1 : Number(value);
                const controlValue = controlValues ? controlValues[tableIndex] : quote[mappingData.control[index]];

                // Zeros don't need to be posted, clutters books, crts
                if (!excludeZeros || (excludeZeros && amount !== 0)) {
                  data.push([field.label, getGlAccount(mappingData.account[index]), controlValue, dealId, amount, isDebit]);
                  runningTotal += Math.round(amount * 100);
                }
              });
            }
          }
        } else {
          const fieldValue = Number(_.get(quote, fieldName) || 0);
          const amount = !isDebit && fieldValue !== 0 ? fieldValue * -1 : fieldValue;

          // Zeros don't need to be posted, clutters books, crts
          if (!excludeZeros || (excludeZeros && amount !== 0)) {
            data.push([
              field.label,
              getGlAccount(mappingData.account[index]),
              quote[mappingData.control[index]],
              dealId,
              amount,
              isDebit,
            ]);
            runningTotal += Math.round(amount * 100);
          }
        }
      }
    });

    setPostingData(data);
    setPostingTotal(runningTotal);
  };

  const renderTable = () => {
    const headers = [
      { text: 'Field' },
      { text: 'GL Account' },
      { text: 'Control' },
      { text: 'Reference', hide: true },
      { text: 'Value', type: 'currencyColumn', className: 'right' },
    ];

    return (
      <div className="mb-2" style={{ minHeight: '290px' }}>
        <ModalTable headers={headers} data={postingData} hideToolbar />
        {postingTotal !== 0 && (
          <Alert className="p-2 mt-2" variant="danger">
            <i className="fas fa-exclamation-circle mr-1" /> Entries are unbalanced! [{postingTotal / 100}]
          </Alert>
        )}
      </div>
    );
  };

  const renderBranchSelection = () => {
    return (
      <FormGroup className="form-select">
        <FormInputLabel>Branch</FormInputLabel>
        <FormControl onChange={handleCurrentBranchChange} as="select" value={postingBranch}>
          {user.branches.map(branch => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </FormControl>
      </FormGroup>
    );
  };

  useEffect(() => {
    if (postingTotal !== 0) {
      console.log('Posting Total', postingTotal / 100);
    }
  }, [postingTotal]);

  useEffect(() => {
    refresh();
  }, [loading, options]);

  useEffect(() => {
    if (mappingValue) {
      const mappingEntry = options.find(o => o.id === mappingValue);
      if (mappingEntry) {
        setPostingSource(mappingEntry.source);
      }

      compileData();
    }
  }, [mappingValue, excludeZeros]);

  useEffect(() => {
    fetchAccounting().then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [postingBranch]);

  useEffect(() => {
    const documentTitleDate = _.get(quote, 'data.documentTitleDate');

    if (documentTitleDate) {
      setPostingDate(documentTitleDate);
    }
  }, [quote]);

  if (loading) {
    return <LoadingPage />;
  }

  if (!quote || !quote.Vehicle) {
    return (
      <FileNotFound className="text-center">
        Quote isn't ready to be posted to accounting. The selected unit is invalid or missing.
      </FileNotFound>
    );
  }

  if (options.length === 0) {
    return (
      <div className="post2acct">
        <Row className="form-edit-container flex-row">
          <Col md={12}>{renderBranchSelection()}</Col>
        </Row>
        <div className="missing-mapping" style={{ height: '390px' }}>
          <FileNotFound className="text-center">
            A matching mapping entry for this vehicle type was not found!
            <br />
            <a href="#" onClick={handleNewMapping}>
              Click Here
            </a>{' '}
            to setup a new entry for {quote.Vehicle['New-Used']} {quote.Vehicle.Type} for this branch.
          </FileNotFound>
        </div>
      </div>
    );
  }

  return (
    <div className="post2acct">
      {postLog.length > 0 && (
        <div className="post-info">
          <i className="fad fa-info-circle" /> <strong>Q{quoteId}</strong>&nbsp;was already posted to accounting
          on&nbsp;
          <strong>{moment(postLog[0].createdAt).format('MMMM Do YYYY, h:mm:ss a')}</strong>.
        </div>
      )}
      <>
        <Row className="form-edit-container flex-row">
          <Col md={6}>{renderBranchSelection()}</Col>

          <Col md={6} className="control-wrapper">
            <FormInputCalendarItem
              controlId="posting-date"
              field={{ label: 'Posting Date', inputProps: { showToday: true } }}
              value={postingDate}
              onChange={handlePostingDateChange}
            />
          </Col>
          <Col md={6} className="control-wrapper">
            <FormInputListSearch
              key={postingSource}
              controlId="posting-source"
              // onChange={handlePostingSourceChange}
              search={{ page: 'AMF3' }}
              field={{ label: 'Posting Source' }}
              onSuggestionSelected={handlePostingSourceSelected}
              value={postingSource}
            />
          </Col>
          <Col md={6} className="d-flex align-items-center">
            <FormCheck className="d-inline-block pl-0 pt-1">
              <FormCheck.Input
                type="checkbox"
                id="chk-zero"
                onChange={() => setExcludeZeros(!excludeZeros)}
                checked={excludeZeros}
              />
              <FormCheck.Label htmlFor="chk-zero">Exclude $0.00 Entries</FormCheck.Label>
            </FormCheck>
          </Col>
        </Row>
        <div className="mapping-selection">
          <div className="mb-1">
            <strong>Select a mapping to apply</strong>
          </div>
          <div className="d-flex">
            <BlazeInputReactSelect
              key={mappingValue} // Hacky
              className="react-select-container flex-grow-1"
              classNamePrefix="react-select"
              onChange={handleMappingChange}
              controlId="mapping-list"
              placeholder="Select Mapping"
              field={{
                name: 'mappingSelection',
                label: '',
                options: options.map(o => {
                  const label = o.title || `${o.condition === 'N' ? 'New' : 'Used'} ${o.type}`;
                  const branch = user.branches.find(b => b.id === Number(o.branchId));

                  return {
                    label: `${label} [${branch.name}]`,
                    value: o.id,
                  };
                }),
              }}
              value={mappingValue}
            />
            {mappingValue && (
              <Button title="Edit Selected Mapping" onClick={handleEditMapping}>
                <i className="fas fa-edit" />
              </Button>
            )}
            <Button variant="success" title="Create New Mapping" onClick={handleNewMapping}>
              <i className="fas fa-plus" />
            </Button>
          </div>
        </div>
      </>

      {mappingValue ? renderTable() : <FileNotFound style={{ height: '300px' }}>No mapping is selected</FileNotFound>}

      <div>
        <div className="float-right">
          <Button variant="default" className="mr-2" onClick={() => modal.close()}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePost} disabled={!mappingValue || !readyToSubmit}>
            Confirm
          </Button>
        </div>
        {mappingValue && (
          <FormCheck className="d-inline-block pl-0 pt-1">
            <FormCheck.Input
              type="checkbox"
              id="chk-lg"
              onChange={() => setReadyToSubmit(!readyToSubmit)}
              checked={readyToSubmit}
            />
            <FormCheck.Label htmlFor="chk-lg">Looks good</FormCheck.Label>
          </FormCheck>
        )}
      </div>
    </div>
  );
};

export default PostToAccounting;
